import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Gavel, Database, Users, Briefcase, Sparkles, Trash2, PlayCircle,
  ArrowRight, ShieldCheck, Stethoscope, X, BadgeInfo,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { can } from "../../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-border bg-white px-4 py-3">
    <p className="font-serif text-2xl text-navy">{value ?? 0}</p>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
  </div>
);

const Chips = ({ title, items }) => (
  items && items.length > 0 ? (
    <div>
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gold-dark">{title}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((t, i) => <span key={i} className="rounded-full bg-navy/5 px-2.5 py-0.5 text-[12px] text-navy">{t}</span>)}
      </div>
    </div>
  ) : null
);

const CandidateIntel = ({ intel, tags }) => (
  <div className="space-y-3" data-testid="candidate-intel-output">
    <div>
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gold-dark">Intelligence Summary</p>
      <p className="text-sm leading-relaxed text-charcoal/85">{intel.intelligence_summary}</p>
    </div>
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="rounded-lg border border-border px-3 py-2"><p className="text-[11px] uppercase text-muted-foreground">Seniority</p><p className="text-sm text-navy">{intel.seniority || "—"}</p></div>
      <div className="rounded-lg border border-border px-3 py-2"><p className="text-[11px] uppercase text-muted-foreground">Total experience</p><p className="text-sm text-navy">{intel.total_experience || "—"}</p></div>
    </div>
    <Chips title="Core skills" items={intel.core_skills} />
    <Chips title="Qualifications" items={intel.qualifications} />
    <Chips title="Certifications" items={intel.certifications} />
    <Chips title="Specialties" items={intel.specialties} />
    <Chips title="Experience highlights" items={intel.experience_highlights} />
    <Chips title="Key data points" items={intel.key_data_points} />
    <Chips title="Work-rights signals" items={intel.work_rights_signals} />
    {tags?.length > 0 && <Chips title="Work-rights tags (derived)" items={tags} />}
  </div>
);

const JobIntel = ({ intel, tags }) => (
  <div className="space-y-3" data-testid="job-intel-output">
    <div>
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gold-dark">Standardised Summary</p>
      <p className="text-sm leading-relaxed text-charcoal/85">{intel.standardized_summary}</p>
    </div>
    <div className="grid gap-3 sm:grid-cols-3">
      <div className="rounded-lg border border-border px-3 py-2"><p className="text-[11px] uppercase text-muted-foreground">Specialty</p><p className="text-sm text-navy">{intel.specialty || "—"}</p></div>
      <div className="rounded-lg border border-border px-3 py-2"><p className="text-[11px] uppercase text-muted-foreground">Facility</p><p className="text-sm text-navy">{intel.facility_type || "—"}</p></div>
      <div className="rounded-lg border border-border px-3 py-2"><p className="text-[11px] uppercase text-muted-foreground">Placement</p><p className="text-sm text-navy">{intel.placement_type || "—"}</p></div>
    </div>
    <Chips title="Must-have requirements" items={intel.must_have_requirements} />
    <Chips title="Nice to have" items={intel.nice_to_have} />
    <Chips title="Required certifications" items={intel.required_certifications} />
    <Chips title="Key requirements" items={intel.key_requirements} />
    <Chips title="Work-rights required" items={intel.work_rights_required} />
    {tags?.length > 0 && <Chips title="Work-rights tags (derived)" items={tags} />}
  </div>
);

const MatchSummary = ({ m, onOpenMatch }) => (
  <div className="rounded-xl border border-navy/20 bg-navy/[0.03] p-4" data-testid="demo-match-summary">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-navy">
        <span className="font-serif">{m.candidate_name?.replace("DEMO DATA – DELETE ANYTIME · ", "")}</span>
        <ArrowRight className="h-4 w-4 text-gold" />
        <span className="font-serif">{m.job_title}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className={`inline-flex h-12 w-12 items-center justify-center rounded-full text-base font-bold text-white ${m.score >= 90 ? "bg-success" : m.score >= 70 ? "bg-gold" : "bg-amber-500"}`}>{m.score}%</span>
        <span className="text-xs font-semibold text-charcoal/70">{m.verdict}</span>
      </div>
    </div>
    <p className="mt-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy"><Gavel className="h-3.5 w-3.5" /> The Judge — Rationale</p>
    <p className="mt-1 text-sm leading-relaxed text-charcoal/85">{m.alignment_summary}</p>
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      <Chips title="Competencies matched" items={m.competencies_matched} />
      <Chips title="Gaps" items={m.gaps} />
    </div>
    {onOpenMatch && m.status === "Pending Review" && (
      <button onClick={() => onOpenMatch(m.id)} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-navy px-4 py-2 text-xs font-semibold text-white hover:bg-navy-light" data-testid="demo-open-match">
        Review in Approval Queue <ArrowRight className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

export default function JudgeKnowledgeBase({ currentUser, onOpenMatch }) {
  const [stats, setStats] = useState(null);
  const [cands, setCands] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [view, setView] = useState("candidates");
  const [detail, setDetail] = useState(null); // {type, data}
  const pollRef = useRef(null);
  const manage = can(currentUser, "judge_db", "manage");

  const load = useCallback(async () => {
    try {
      const [s, c, j, d] = await Promise.all([
        api.get("/admin/judge/stats"),
        api.get("/admin/judge/candidates"),
        api.get("/admin/judge/jobs"),
        api.get("/admin/judge/demo/status"),
      ]);
      setStats(s.data); setCands(c.data); setJobs(j.data); setDemo(d.data);
      return d.data;
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  // poll while demo is processing
  useEffect(() => {
    clearInterval(pollRef.current);
    if (demo?.processing) {
      pollRef.current = setInterval(async () => {
        const d = await load();
        if (d && !d.processing) { clearInterval(pollRef.current); if (d.match) toast.success("Demo judged — knowledge base populated."); }
      }, 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [demo?.processing, load]);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/judge/demo/seed");
      if (data.seeded === false) toast.message(data.message);
      else if (data.ai_available === false) toast.error("Demo records created, but AI engine is offline (LLM key inactive).");
      else toast.info("Demo seeded — agents are analysing the CV and job now…");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSeeding(false); }
  };

  const purgeDemo = async () => {
    if (!window.confirm("Delete all DEMO DATA (candidate, job, intelligence and match)? This cannot be undone.")) return;
    try { await api.delete("/admin/judge/demo"); toast.success("Demo data deleted."); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;

  const rows = view === "candidates" ? cands : jobs;

  return (
    <div className="space-y-6" data-testid="judge-kb-panel">
      {/* Agent header */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-navy to-navy-light p-6 text-white">
        <div className="flex items-center gap-2"><Gavel className="h-5 w-5 text-gold" /><h2 className="font-serif text-xl">AI Matching Judge — Knowledge Base</h2>
          <span className="ml-2 rounded-full bg-success/20 px-2.5 py-0.5 text-[11px] font-bold text-green-200">Agent #11 · Active</span>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-white/70">
          The Matching Judge never reads raw CVs or job descriptions. The Candidate Intelligence Agent (#4) and Job
          Intelligence Agent (#9) convert each into <b className="text-white">structured intelligence</b>, stored in this
          dedicated vault. The Judge cross-analyses only this structured data to score, rank and shortlist candidates —
          then routes matches to your Approval Queue. Threshold ≥ {stats?.threshold}% → Pending Review.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Candidate Intel" value={stats?.candidates_ready} />
        <Stat label="Job Intel" value={stats?.jobs_ready} />
        <Stat label="Matches Judged" value={stats?.matches} />
        <Stat label="Pending Review" value={stats?.pending} />
        <Stat label="Match Threshold" value={`${stats?.threshold}%`} />
      </div>

      {/* Demo panel */}
      <div className="rounded-2xl border-2 border-dashed border-gold/60 bg-gold/[0.04] p-5" data-testid="demo-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BadgeInfo className="h-5 w-5 text-gold-dark" />
            <h3 className="font-serif text-lg text-navy">Demo Workflow — <span className="text-gold-dark">DEMO DATA · DELETE ANYTIME</span></h3>
          </div>
          <div className="flex items-center gap-2">
            {!demo?.exists && manage && (
              <button onClick={seedDemo} disabled={seeding} className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-70" data-testid="demo-seed-btn">
                {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Seed Demo Candidate + Job
              </button>
            )}
            {demo?.exists && manage && (
              <button onClick={purgeDemo} className="inline-flex items-center gap-2 rounded-lg border border-destructive px-4 py-2 text-sm font-semibold text-destructive hover:bg-destructive/5" data-testid="demo-delete-btn">
                <Trash2 className="h-4 w-4" /> Delete Demo Data
              </button>
            )}
          </div>
        </div>

        {!demo?.exists && (
          <p className="mt-2 text-sm text-muted-foreground">Seed a realistic ICU nurse candidate and a matching ICU job to see the full pipeline: Candidate Intelligence → Job Intelligence → Judge score, ranking & recommendation. Completely separate from production; delete with one click before go-live.</p>
        )}

        {demo?.exists && demo?.processing && (
          <div className="mt-4 flex items-center gap-2 text-sm text-navy"><Loader2 className="h-4 w-4 animate-spin text-gold" /> Agents are analysing the demo CV and job description…{demo?.ai_available === false && " (AI engine offline — enable the LLM key to populate intelligence)"}</div>
        )}

        {demo?.exists && !demo?.processing && (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="mb-2 flex items-center gap-2 text-navy"><Users className="h-4 w-4 text-gold" /><span className="font-serif">Candidate Intelligence Output</span></div>
              {demo.candidate_intel?.intel ? <CandidateIntel intel={demo.candidate_intel.intel} tags={demo.candidate_intel.work_rights_tags} /> : <p className="text-sm text-muted-foreground">Not generated yet.</p>}
            </div>
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="mb-2 flex items-center gap-2 text-navy"><Briefcase className="h-4 w-4 text-gold" /><span className="font-serif">Job Intelligence Output</span></div>
              {demo.job_intel?.intel ? <JobIntel intel={demo.job_intel.intel} tags={demo.job_intel.work_rights_required_tags} /> : <p className="text-sm text-muted-foreground">Not generated yet.</p>}
            </div>
            <div className="lg:col-span-2">
              {demo.match ? <MatchSummary m={demo.match} onOpenMatch={onOpenMatch} /> : <p className="text-sm text-muted-foreground">Judge has not produced a match yet.</p>}
            </div>
          </div>
        )}
      </div>

      {/* Vault browser */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <Database className="h-5 w-5 text-navy" />
          <h3 className="font-serif text-lg text-navy">Structured Intelligence Vault</h3>
        </div>
        <div className="mb-4 inline-flex rounded-lg bg-secondary p-1">
          <button onClick={() => setView("candidates")} className={`rounded-md px-4 py-1.5 text-sm font-semibold ${view === "candidates" ? "bg-navy text-white" : "text-charcoal/70"}`} data-testid="kb-tab-candidates">Candidate Intelligence ({cands.length})</button>
          <button onClick={() => setView("jobs")} className={`rounded-md px-4 py-1.5 text-sm font-semibold ${view === "jobs" ? "bg-navy text-white" : "text-charcoal/70"}`} data-testid="kb-tab-jobs">Job Intelligence ({jobs.length})</button>
        </div>
        {rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-white py-14 text-center text-sm text-muted-foreground" data-testid="kb-empty">No structured intelligence yet. Run the engine or seed the demo above.</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((r) => (
              <button key={r.candidate_id || r.job_id} onClick={() => setDetail({ type: view, data: r })} className="rounded-2xl border border-border bg-white p-4 text-left hover:border-navy" data-testid={`kb-card-${r.candidate_id || r.job_id}`}>
                <p className="font-serif text-navy">{(r.candidate_name || r.job_title || "—")}</p>
                <p className="text-xs text-muted-foreground">{r.profession || ""}{r.company_name ? ` · ${r.company_name}` : ""}</p>
                <p className="mt-2 line-clamp-3 text-sm text-charcoal/75">{r.intel?.intelligence_summary || r.intel?.standardized_summary}</p>
                <p className="mt-2 text-[11px] font-semibold text-gold-dark">View structured intelligence →</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-navy">{detail?.data?.candidate_name || detail?.data?.job_title}</DialogTitle></DialogHeader>
          {detail && (detail.type === "candidates"
            ? <CandidateIntel intel={detail.data.intel} tags={detail.data.work_rights_tags} />
            : <JobIntel intel={detail.data.intel} tags={detail.data.work_rights_required_tags} />)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
